// ═══════════════════════════════════════════════════════════
// Public Library API — standalone document library
//
// Separate from FOIA request documents. These are general
// government documents (council minutes, budgets, reports)
// that staff publish directly for public access.
// ═══════════════════════════════════════════════════════════

import { queryRecords, createRecord, updateRecord, deleteRecord } from './client';
import { TABLES } from './tables';
import { FIELDS } from './fields';
import { uploadDocument, buildStorageUrl, getDownloadUrlFromField } from './documents';

const F = FIELDS.libraryDocuments;

export interface LibraryDocument {
  recordId: number;
  title: string;
  description: string;
  category: string;
  fileName: string;
  fileSize: number;
  mimeType: string;
  storageUrl: string;
  uploadedBy: string;
  uploadedAt: string;
  isPublished: boolean;
}

function rowToDoc(row: Record<string, unknown>): LibraryDocument {
  return {
    recordId:    row['record_id'] as number,
    title:       String(row[F.title] ?? ''),
    description: String(row[F.description] ?? ''),
    category:    String(row[F.category] ?? ''),
    fileName:    String(row[F.fileName] ?? ''),
    fileSize:    Number(row[F.fileSize] ?? 0),
    mimeType:    String(row[F.mimeType] ?? ''),
    storageUrl:  String(row[F.storageUrl] ?? ''),
    uploadedBy:  String(row[F.uploadedBy] ?? ''),
    uploadedAt:  String(row[F.uploadedAt] ?? ''),
    isPublished: row[F.isPublished] === 'true' || row[F.isPublished] === true,
  };
}

// ── List all library documents (staff) ──────────────────
export async function listLibraryDocuments(): Promise<LibraryDocument[]> {
  const { records } = await queryRecords(TABLES.libraryDocuments, {
    sort: { field: F.uploadedAt, direction: 'desc' },
    limit: 500,
  });
  return (records as Record<string, unknown>[]).map(rowToDoc);
}

// ── List published only (public portal) ─────────────────
export async function listPublishedLibraryDocuments(opts?: { anonymous?: boolean }): Promise<LibraryDocument[]> {
  const { records } = await queryRecords(TABLES.libraryDocuments, {
    filters: [{ field: F.isPublished, operator: 'eq', value: 'true' }],
    sort: { field: F.uploadedAt, direction: 'desc' },
    limit: 500,
  }, opts);
  return (records as Record<string, unknown>[]).map(rowToDoc);
}

// ── Create + upload ─────────────────────────────────────
export async function createLibraryDocument(input: {
  title: string;
  description?: string;
  category: string;
  file: File;
  uploadedBy: string;
}): Promise<LibraryDocument> {
  const now = new Date().toISOString();

  // Upload file to GAB storage
  const ref = await uploadDocument(input.file);
  const storageUrl = buildStorageUrl(ref.id);

  const row = await createRecord(TABLES.libraryDocuments, {
    [F.title]:       input.title,
    [F.description]: input.description ?? '',
    [F.category]:    input.category,
    [F.fileName]:    input.file.name,
    [F.fileSize]:    String(input.file.size),
    [F.mimeType]:    input.file.type || 'application/octet-stream',
    [F.storageUrl]:  storageUrl,
    [F.uploadedBy]:  input.uploadedBy,
    [F.uploadedAt]:  now,
    [F.isPublished]: 'true',
  });
  return rowToDoc(row as Record<string, unknown>);
}

// ── Update metadata ─────────────────────────────────────
export async function updateLibraryDocument(id: number, data: Partial<{
  title: string;
  description: string;
  category: string;
  isPublished: boolean;
}>): Promise<void> {
  const fields: Record<string, unknown> = {};
  if (data.title !== undefined) fields[F.title] = data.title;
  if (data.description !== undefined) fields[F.description] = data.description;
  if (data.category !== undefined) fields[F.category] = data.category;
  if (data.isPublished !== undefined) fields[F.isPublished] = String(data.isPublished);
  await updateRecord(TABLES.libraryDocuments, id, fields);
}

// ── Delete ──────────────────────────────────────────────
export async function deleteLibraryDocument(id: number): Promise<void> {
  await deleteRecord(TABLES.libraryDocuments, id);
}

// ── Get download URL ────────────────────────────────────
export async function getLibraryDownloadUrl(doc: LibraryDocument, opts?: { anonymous?: boolean }): Promise<string> {
  const url = await getDownloadUrlFromField(doc.storageUrl, opts);
  return url ?? '';
}
